<template>
  <a-modal
    v-model:visible="showEditModulesModal"
    centered
    title="Edit Kaizen Modules"
    :confirm-loading="savingModules"
    @ok="handleSaveModules"
    @cancel="handleCloseModulesModal"
    okText="Save Changes"
    cancelText="Cancel"
    width="700px"
    destroyOnClose
  >
    <a-spin :spinning="loadingModules">
      <div v-if="modulesList.length">
        <a-row justify="space-between" align="middle" class="mb-3">
          <a-typography-text type="secondary">
            Select modules to enable for this organization.
          </a-typography-text>

          <div>
            <a-button size="small" type="link" @click="selectAllModules">
              Select All
            </a-button>
            <a-divider type="vertical" />
            <a-button size="small" type="link" @click="clearAllModules">
              Clear
            </a-button>
          </div>
        </a-row>

        <div class="module-container">
          <a-checkbox-group v-model:value="selectedModules">
            <a-row :gutter="[16, 16]">
              <a-col
                v-for="module in modulesList"
                :key="module.code"
                :xs="24"
                :sm="12"
              >
                <a-card size="small" class="module-card">
                  <a-checkbox :value="module.code">
                    <div class="module-title">
                      {{ module.name }}
                    </div>
                  </a-checkbox>
                </a-card>
              </a-col>
            </a-row>
          </a-checkbox-group>
        </div>
      </div>

      <a-empty v-else-if="!loadingModules" description="No modules available" />
    </a-spin>
  </a-modal>
  <a-modal
    v-model:visible="showOrgStatusModal"
    centered
    title="Update Org Status"
    okText="Update"
    :confirm-loading="updatingOrgStatus"
    @ok="handleSaveOrgStatus"
    @cancel="showOrgStatusModal = false"
    destroyOnClose
  >
    <a-form-item label="Org Status">
      <a-select
        v-model:value="selectedOrgStatus"
        style="width: 100%"
        placeholder="Select a status"
        :options="orgStatusOptions"
      />
    </a-form-item>
  </a-modal>

  <a-modal
    :visible="showDeleteOrg"
    centered
    title="Delete Organization"
    okText="Submit"
    @ok="isConfirm = true"
    @cancel="handleCloseDeleteOrg"
    :okButtonProps="{
      id: 'delete-org-ok-btn',
      loading: deletingOrg,
    }"
    :cancelButtonProps="{
      id: 'delete-org-cancel-btn',
      style: { marginLeft: 'auto' },
    }"
    id="org-deletion-modal"
  >
    <template #closeIcon>
      <close-outlined id="delete-org-close-btn" />
    </template>
    <p>
      Are you sure? you want to delete the organization.<br />
      Please enter organization name to delete.
    </p>
    <div class="d-flex flex-column">
      <a-input class="w-50" id="orgDeleteMsg" v-model:value="orgDeleteMsg" />
      <small class="text-danger" v-if="isError"
        >Please enter correct name!</small
      >
    </div>
  </a-modal>

  <a-modal
    v-model:visible="showUpdateDeviceSettings"
    centered
    :closable="false"
    :title="'Update Device Settings'"
    :footer="null"
    destroyOnClose
  >
    <update-device-settings
      @cancel="handleshowUpdateDeviceSettings"
      :orgName="this.orgToUpdateDeviceSettings"
    >
    </update-device-settings>
  </a-modal>

  <a-modal
    v-model:visible="showUsersModal"
    centered
    :title="'Users - ' + selectedOrgName"
    :footer="null"
    width="900px"
    destroyOnClose
  >
    <template #closeIcon>
      <close-outlined @click="handleCloseUsersModal" />
    </template>
    <a-table
      :columns="userColumns"
      :data-source="usersList"
      :loading="loadingUsers"
      bordered
      :scroll="{ y: '50vh', x: true }"
      :pagination="{ position: ['bottomCenter'], pageSize: 10 }"
    >
      <template #title>
        <a-typography-title :level="5">
          Total Users: &nbsp;
          <a-tag color="blue">{{ usersList.length }}</a-tag>
        </a-typography-title>
      </template>
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'role'">
          {{ record.role?.name || '' }}
        </template>
        <template v-if="column.dataIndex === 'action'">
          <a-popconfirm
            :title="`Are you sure you want to delete user ${record.email}?`"
            ok-text="Yes"
            cancel-text="No"
            @confirm="handleDeleteUser(record.email)"
          >
            <a-button type="primary" danger>
              <template #icon><DeleteOutlined /></template>
              Delete
            </a-button>
          </a-popconfirm>
        </template>
      </template>
    </a-table>
  </a-modal>

  <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" class="d-flex" style="gap: 8px">
    <a-input-search
      v-model:value="searchValue"
      @change="handleSearch"
      @keyup.enter="handleSearch"
      placeholder="Search Organization By Name"
      style="flex: 1"
    />
    <a-select
      v-model:value="selectedParentCompany"
      placeholder="Filter by Parent Company"
      allow-clear
      style="min-width: 220px"
      :options="parentCompanyOptions"
      @change="handleParentCompanyFilter"
      @clear="handleParentCompanyClear"
      :not-found-content="parentCompanies.length === 0 ? 'No parent companies found' : undefined"
    />
  </a-col>

  <a-table
    :columns="orgListColumns"
    :data-source="list"
    bordered
    :pagination="pagination"
    @change="handleTableChange"
    :scroll="{ y: '55vh', x: '1800px' }"
  >
    <template #title>
      <a-typography-title :level="5">
        Organizations: &nbsp;
        <a-tag color="blue">{{ organizationCount }}</a-tag>
      </a-typography-title>
    </template>
    <template #bodyCell="{ column, record }">
      <template v-if="column.dataIndex === 'parent_company'">
        {{ record.parent_company || 'None' }}
      </template>
      <template v-if="column.dataIndex === 'org_status'">
        {{ record.org_status || 'Not Selected Yet' }}
      </template>
      <template v-if="column.dataIndex === 'logo'">
        <img
          v-if="!record.notFound"
          :key="'logo_' + record.org_name_to_display"
          :alt="record.org_name_to_display"
          :src="record.logoImage"
          @error="handleImageError(event, record)"
          width="40"
          height="40"
        />
        <span v-else> Not Found </span>
      </template>
      <template v-if="column.dataIndex === 'use_proxy_server'">
        <a-switch
          :checked="record.use_proxy_server"
          @change="(value) => handleChangeProxySetting(value, record)"
        />
      </template>
      <template v-if="column.dataIndex === 'MaxAllowedUsers'">
        {{ record.MaxAllowedUsers === 0 ? 'No Limit' : record.MaxAllowedUsers }}
      </template>
      <template v-if="column.dataIndex === 'org_creation_date'">
        {{ formatDate(record.org_creation_date) }}
      </template>
      <template v-if="column.dataIndex === 'kaizen_bucket_created'">
        <a-tag :color="record.kaizen_bucket_created ? 'green' : 'red'">
          {{ record.kaizen_bucket_created ? 'Yes' : 'No' }}
        </a-tag>
      </template>
      <template v-if="column.dataIndex === 'actions'">
        <div class="actions">
          <a-dropdown :trigger="['click']">
            <a-button class="ml-2">
              <template #icon>
                <MenuOutlined class="menu-icon" />
              </template>
            </a-button>
            <template #overlay>
              <a-menu>
                <a-menu-item key="1" @click="handleShowUsers(record.Org_name)">
                  <UserOutlined />
                  Show Users
                </a-menu-item>
                <a-menu-item
                  key="2"
                  @click="createKaizenBucket(record.org_name_to_display)"
                >
                  <PlusOutlined />
                  Create Kaizen Bucket
                </a-menu-item>
                <a-menu-item
                  key="3"
                  @click="refreshTrainingVideos(record.org_name_to_display)"
                >
                  <RedoOutlined />
                  Refresh Videos
                </a-menu-item>
                <a-menu-item
                  key="4"
                  @click="
                    handleshowUpdateDeviceSettings(record.org_name_to_display)
                  "
                >
                  <SettingOutlined />
                  Update Device Settings
                </a-menu-item>
                <a-menu-item key="5" @click="onEdit(record)">
                  <EditOutlined />
                  Edit
                </a-menu-item>
                <a-menu-item
                  key="6"
                  @click="handleEditKaizenModules(record.Org_name)"
                >
                  <AppstoreOutlined />
                  Edit Kaizen Modules
                </a-menu-item>
                <a-menu-item
                  key="7"
                  @click="handleOpenOrgStatusModal(record)"
                >
                  <TagsOutlined />
                  Update Org Status
                </a-menu-item>
                <a-menu-item
                  key="8"
                  @click="handleDeleteOrg(record.org_name_to_display)"
                >
                  <DeleteOutlined />
                  Delete
                </a-menu-item>
                <a-menu-item
                  key="9"
                  @click="handleUploadLogoOrg(record.org_name_to_display)"
                >
                  <a-upload
                    accept=".png, .jpg, .jpeg, .ico"
                    :multiple="false"
                    :before-upload="beforeUpload"
                    :custom-request="
                      (fileObj) => handleUploadLogo(fileObj, record)
                    "
                    :show-upload-list="false"
                  >
                    <FileImageOutlined />
                    Upload Logo
                  </a-upload>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script>
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  MenuOutlined,
  PlusOutlined,
  RedoOutlined,
  SettingOutlined,
  TagsOutlined,
  UserOutlined,
  AppstoreOutlined,
} from '@ant-design/icons-vue';
import { Upload } from 'ant-design-vue';
import debounce from 'debounce';
import OrgServices from 'src/services/organization';
import S3Service from 'src/services/s3';
import UserServices from 'src/services/user';
import { mapActions, mapGetters } from 'vuex';
import orgListColumns from './config';
import UpdateDeviceSettings from './UpdateDeviceSettings.vue';

export default {
  name: 'OrganizationComponent',
  components: {
    MenuOutlined,
    RedoOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    SettingOutlined,
    FileImageOutlined,
    CloseOutlined,
    TagsOutlined,
    UserOutlined,
    AppstoreOutlined,
    UpdateDeviceSettings,
  },
  inject: ['toast'],
  emits: ['onEditClick'],
  setup: () => ({ orgListColumns }),
  data() {
    return {
      uploadLogoOrg: '',
      img_url:
        'https://retrocausal-video-thumbnails.s3.us-east-2.amazonaws.com/logos/',
      list: [],
      orgDeleteMsg: '',
      searchValue: '',
      parentCompanies: [],
      selectedParentCompany: null,
      orgToDelete: null,
      orgToUpdateDeviceSettings: null,
      showDeleteOrg: false,
      isConfirm: null,
      isError: false,
      deletingOrg: false,
      showUpdateDeviceSettings: false,
      showUsersModal: false,
      usersList: [],
      loadingUsers: false,
      selectedOrgName: '',
      userColumns: [
        {
          title: 'Email',
          dataIndex: 'email',
        },
        {
          title: 'First Name',
          dataIndex: 'username',
        },
        {
          title: 'Last Name',
          dataIndex: 'lastname',
        },
        {
          title: 'Role',
          dataIndex: 'role',
        },
        {
          title: 'Action',
          dataIndex: 'action',
        },
      ],
      // Org Status Edit State
      showOrgStatusModal: false,
      selectedOrgStatus: null,
      updatingOrgStatus: false,
      orgForStatusEdit: null,
      // Kaizen Modules Edit State
      showEditModulesModal: false,
      modulesList: [],
      selectedModules: [],
      loadingModules: false,
      savingModules: false,
      orgForModulesEdit: null,
    };
  },
  computed: {
    ...mapGetters(['currentOrgPage', 'organizationList', 'organizationCount']),

    parentCompanyOptions() {
      return this.parentCompanies.map((tag) => ({ label: tag, value: tag }));
    },

    orgStatusOptions() {
      return [
        { label: 'Active Customer', value: 'Active Customer' },
        { label: 'Test Org', value: 'Test Org' },
        { label: 'POC', value: 'POC' },
        { label: 'Inactive Customer', value: 'Inactive Customer' },
      ];
    },

    pagination() {
      return {
        position: ['bottomCenter'],
        showSizeChanger: false,
        current: this.currentOrgPage,
        total: this.organizationCount,
      };
    },
  },
  watch: {
    async isConfirm(value) {
      if (value === null) return;
      if (value) {
        if (this.orgDeleteMsg !== this.orgToDelete) {
          this.isError = true;
          this.isConfirm = null;
          return;
        }
        this.deleteOrg();
        this.isError = false;
        this.isConfirm = null;
        this.orgDeleteMsg = '';
      } else {
        this.taskUpdateMsg = '';
        this.isError = false;
      }
      this.showDeleteOrg = false;
      this.isConfirm = false;
    },
    organizationList(value) {
      this.list = value;
    },
    currentOrgPage() {
      this.getPaginatedOrganizations(this.searchValue);
    },
  },
  async created() {
    this.getPaginatedOrganizations(this.searchValue);
    const [, data] = await OrgServices.getParentCompanies();
    if (Array.isArray(data)) this.parentCompanies = data;
  },
  methods: {
    ...mapActions([
      'deleteSingleOrg',
      'refreshVideos',
      'getPaginatedOrganizations',
      'handlePageChange',
    ]),

    handleshowUpdateDeviceSettings(orgName) {
      this.orgToUpdateDeviceSettings = orgName;
      this.showUpdateDeviceSettings = !this.showUpdateDeviceSettings;
    },

    handleDeleteOrg(orgName) {
      this.orgToDelete = orgName;
      this.showDeleteOrg = true;
    },

    async deleteOrg() {
      this.deletingOrg = true;
      await this.deleteSingleOrg(this.orgToDelete);
      await this.deleteLogoFile(this.orgToDelete);
      this.getPaginatedOrganizations(this.searchValue);
      this.deletingOrg = false;
    },

    onEdit(rowData) {
      this.$emit('onEditClick', rowData);
    },

    beforeUpload(file) {
      const validExtensions = ['png', 'jpg', 'jpeg', 'ico'];
      const extension = file.name.split('.').pop().toLowerCase();

      if (!validExtensions.includes(extension)) {
        this.toast.error('Invalid File');
        return Upload.LIST_IGNORE;
      }
    },

    handleUploadLogoOrg(org_name) {
      this.uploadLogoOrg = org_name;
    },

    async handleUploadLogo(fileObj, obj) {
      const { file } = fileObj;
      var payload = new FormData();
      const bucket = 'retrocausal-video-thumbnails';
      const filePath = `logos/${this.uploadLogoOrg}.jpg`;

      payload.append('file', file);
      payload.append('bucket', bucket);
      payload.append('file_path', filePath);

      const [Error] = await S3Service.uploadFile(payload, false);
      if (Error) {
        this.toast.error('Failed to upload organization logo');
        return;
      }
      this.updateOrgInList(obj.org_name_to_display, {
        logoImage:
          this.img_url + obj.org_name_to_display + '.jpg?t=' + Date.now(),
        notFound: false,
      });
      this.toast.success('Successfully uploaded organization logo');
    },

    async deleteLogoFile(orgName) {
      const payload = {
        bucket: 'retrocausal-video-thumbnails',
        file_path: `logos/${orgName}.jpg`,
        organization: orgName,
      };
      await S3Service.deleteFile(payload, false);
    },

    handleImageError(event, record) {
      record.notFound = true;
      record.logoImage = '';
    },

    async refreshTrainingVideos(orgName) {
      const res = await this.refreshVideos(orgName);
      if (res === 'error') {
        this.toast.error('Error occured while refreshing videos!');
        return;
      }
      this.toast.success(`${res.body} videos refreshed!`);
    },

    async createKaizenBucket(orgName) {
      const payload = {
        organization: orgName,
      };
      const [error, data] = await OrgServices.createKaizenBucket(payload);
      if (error) {
        const errMsg = error.response.data['response'];
        this.toast.info(errMsg);
        return;
      }
      this.toast.success(data.response);
    },

    handleTableChange(pagination) {
      const { current } = pagination;
      this.handlePageChange(current);
    },

    updateOrgInList(orgName, updatedObj) {
      const temp = [...this.list];
      const index = temp.findIndex((v) => v.org_name_to_display === orgName);
      if (temp[index]) {
        temp[index] = {
          ...temp[index],
          ...updatedObj,
        };
      }
      this.list = temp;
    },

    handleCloseDeleteOrg() {
      this.isConfirm = false;
      this.orgDeleteMsg = '';
    },

    handleSearch: debounce(async function () {
      this.selectedParentCompany = null;
      if (this.currentOrgPage === 1)
        this.getPaginatedOrganizations(this.searchValue);
      else this.handlePageChange(1);
    }, 500),

    async handleParentCompanyFilter(tag) {
      if (!tag) return;
      this.searchValue = '';
      const [, data] = await OrgServices.getOrganizationsByTag(tag);
      if (data?.results) {
        this.list = data.results.map((org) => ({
          ...org,
          logoImage: this.img_url + org.org_name_to_display + '.jpg',
        }));
      }
    },

    handleParentCompanyClear() {
      this.getPaginatedOrganizations(this.searchValue);
    },

    async handleChangeProxySetting(value, record) {
      record.use_proxy_server = value;

      await OrgServices.setProxyServerSetting(
        value,
        record.org_name_to_display
      );
      const recordIndex = this.list.findIndex(
        (org) => org.org_name_to_display === record.org_name_to_display
      );
      this.list[recordIndex] = {
        ...this.list[recordIndex],
        use_proxy_server: value,
      };
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    async handleShowUsers(organizationId) {
      this.selectedOrgName = organizationId;
      this.showUsersModal = true;
      this.loadingUsers = true;
      this.usersList = [];

      try {
        const [error, data] = await UserServices.getUsersByOrganization(
          organizationId
        );

        if (error) {
          this.toast.error('Failed to fetch users for this organization');
          this.usersList = [];
        } else {
          this.usersList = Array.isArray(data) ? data : [];
        }
      } catch (err) {
        this.toast.error('Failed to fetch users for this organization');
        this.usersList = [];
      } finally {
        this.loadingUsers = false;
      }
    },

    handleCloseUsersModal() {
      this.showUsersModal = false;
      this.usersList = [];
      this.selectedOrgName = '';
    },

    async handleDeleteUser(userEmail) {
      const [error] = await UserServices.deleteUser(userEmail);

      if (error) {
        this.toast.error('Failed to delete user');
        return;
      }

      this.toast.success('User deleted successfully');
      this.usersList = this.usersList.filter((u) => u.email !== userEmail);
    },

    async handleEditKaizenModules(orgName) {
      this.orgForModulesEdit = orgName;
      this.showEditModulesModal = true;
      this.loadingModules = true;
      this.modulesList = [];
      this.selectedModules = [];

      try {
        const [
          [allModulesError, allModulesData],
          [userModulesError, userModulesData],
        ] = await Promise.all([
          OrgServices.getKaizenModules(),
          OrgServices.getUserModules(orgName),
        ]);

        if (allModulesError) {
          this.toast.error('Failed to load Kaizen modules');
          return;
        }

        if (userModulesError) {
          this.toast.error('Failed to load organization modules');
          return;
        }

        // Load all modules
        this.modulesList = Array.isArray(allModulesData) ? allModulesData : [];

        // Preselect previously selected modules
        this.selectedModules = Array.isArray(userModulesData?.modules)
          ? userModulesData.modules
          : [];
      } catch (err) {
        this.toast.error('Something went wrong');
      } finally {
        this.loadingModules = false;
      }
    },

    async handleSaveModules() {
      this.savingModules = true;

      const payload = {
        modules: this.selectedModules, // array of codes
      };

      const [error] = await OrgServices.updateKaizenModules(
        this.orgForModulesEdit,
        payload
      );

      if (error) {
        this.toast.error('Failed to update modules');
        this.savingModules = false;
        return;
      }

      this.toast.success('Kaizen modules updated successfully');
      this.savingModules = false;
      this.handleCloseModulesModal();
    },

    selectAllModules() {
      this.selectedModules = this.modulesList.map((m) => m.code);
    },

    clearAllModules() {
      this.selectedModules = [];
    },

    handleOpenOrgStatusModal(record) {
      this.orgForStatusEdit = record;
      this.selectedOrgStatus = record.org_status || null;
      this.showOrgStatusModal = true;
    },

    async handleSaveOrgStatus() {
      if (!this.selectedOrgStatus) {
        this.toast.error('Please select a status');
        return;
      }
      this.updatingOrgStatus = true;
      const [error] = await OrgServices.updateOrganization(
        this.orgForStatusEdit.org_name_to_display,
        { org_status: this.selectedOrgStatus }
      );
      this.updatingOrgStatus = false;
      if (error) {
        this.toast.error('Failed to update org status');
        return;
      }
      this.updateOrgInList(this.orgForStatusEdit.org_name_to_display, {
        org_status: this.selectedOrgStatus,
      });
      this.toast.success('Org status updated successfully');
      this.showOrgStatusModal = false;
      this.orgForStatusEdit = null;
    },

    handleCloseModulesModal() {
      this.showEditModulesModal = false;
      this.modulesList = [];
      this.selectedModules = [];
      this.orgForModulesEdit = null;
    },
  },
};
</script>

<style scoped>
.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin: 0 6px;
}

.error-msg {
  color: red;
}

.error-box {
  width: 140px;
  margin-left: 20px;
}
.module-container {
  margin-top: 16px;
}

.module-card {
  border-radius: 12px;
  transition: all 0.25s ease;
  cursor: pointer;
}

.module-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.module-title {
  font-weight: 500;
  font-size: 14px;
}
</style>
